import React from "react";
import { Professional } from "../../../types";
import { Card, StepIndicatorHeader, ActionButton } from "../../shared";

interface ProfessionalSelectorProps {
  professionals: Professional[];
  selectedProfessional: Professional | null;
  onProfessionalSelect: (professional: Professional) => void;
  onContinue?: () => void;
  loading?: boolean;
}

export const ProfessionalSelector: React.FC<ProfessionalSelectorProps> = ({
  professionals,
  selectedProfessional,
  onProfessionalSelect,
  onContinue,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card>
        <div className="space-y-4 sm:space-y-6">
          <StepIndicatorHeader
            stepNumber={2}
            title="Escolha um profissional"
            description="Selecione o profissional que irá atender você"
          />
          <div className="flex flex-col items-center gap-4 py-8 sm:py-12 text-gray-600">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm sm:text-base">Carregando profissionais...</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!professionals.length) {
    return (
      <Card>
        <div className="space-y-4 sm:space-y-6">
          <StepIndicatorHeader
            stepNumber={2}
            title="Escolha um profissional"
            description="Selecione o profissional que irá atender você"
          />
          <p className="text-center text-gray-600 py-8 sm:py-12 text-base sm:text-lg">
            Nenhum profissional disponível
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
        <StepIndicatorHeader
          stepNumber={2}
          title="Escolha um profissional"
          description="Selecione o profissional que irá atender você"
        />
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-4 sm:mt-6">
          {professionals.map((professional) => {
            const isSelected = selectedProfessional?.id === professional.id;
            return (
              <button
                key={professional.id}
                className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 md:p-5 bg-white border-2 rounded-xl cursor-pointer transition-all duration-300 text-left w-full sm:w-auto sm:min-w-[280px] sm:max-w-[320px] ${
                  isSelected
                    ? "border-sky-500 bg-gradient-to-br from-sky-50 to-sky-100 shadow-lg shadow-sky-200/50"
                    : "border-gray-200 hover:border-sky-400 hover:shadow-md hover:-translate-y-0.5 hover:shadow-sky-200/30"
                }`}
                onClick={() => onProfessionalSelect(professional)}
              >
                <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full overflow-hidden">
                  {professional.picture ? (
                    <img
                      src={professional.picture}
                      alt={professional.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-sky-500 to-sky-600 text-white flex items-center justify-center text-2xl sm:text-3xl md:text-4xl font-semibold">
                      {professional.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-0.5 sm:mb-1 truncate">
                    {professional.name}
                  </h4>
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Continue Button - Show when professional is selected */}
        {selectedProfessional && onContinue && (
          <div className="mt-6 sm:mt-8 text-center">
            <ActionButton
              onClick={onContinue}
              variant="primary"
              size="md"
              className="inline-flex items-center gap-2"
            >
              Continuar
              <span className="text-lg">→</span>
            </ActionButton>
          </div>
        )}
    </>
  );
};
